import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Filter, Monitor } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'all', label: 'Tous' },
  { id: 'website', label: 'Sites Web' },
  { id: 'ecommerce', label: 'E-Commerce' },
  { id: 'software', label: 'Logiciels' },
  { id: 'saas', label: 'SaaS' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// iPhone Mockup Component for Mobile Apps
const IPhoneMockup = ({ item }) => {
  return (
    <motion.div
      variants={itemVariants}
      className="group"
      data-testid={`portfolio-mobile-${item.id}`}
    >
      <div className="glass-card rounded-3xl p-8 flex flex-col items-center">
        {/* iPhone Frame */}
        <div className="relative mb-6">
          {/* iPhone outer frame */}
          <div className="relative w-[200px] h-[410px] bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-[40px] p-[10px] shadow-2xl border border-[#3a3a3a]">
            {/* iPhone inner frame */}
            <div className="relative w-full h-full bg-black rounded-[30px] overflow-hidden">
              {/* Screen content - full image without notch */}
              <div className="w-full h-full overflow-hidden rounded-[30px]">
                <img 
                  src={item.image_url} 
                  alt={item.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              {/* Subtle top bar overlay */}
              <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
            </div>
            
            {/* Side button */}
            <div className="absolute right-[-2px] top-24 w-[3px] h-12 bg-[#3a3a3a] rounded-l-sm" />
            <div className="absolute left-[-2px] top-20 w-[3px] h-8 bg-[#3a3a3a] rounded-r-sm" />
            <div className="absolute left-[-2px] top-32 w-[3px] h-16 bg-[#3a3a3a] rounded-r-sm" />
          </div>
          
          {/* Glow effect */}
          <div className="absolute inset-0 bg-[#8B5A8B]/20 blur-3xl rounded-full -z-10 group-hover:bg-[#8B5A8B]/30 transition-colors duration-500" />
        </div>

        {/* App info */}
        <div className="text-center w-full">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Smartphone className="text-[#8B5A8B]" size={18} />
            <span className="text-xs text-[#8B5A8B] font-medium uppercase tracking-wider">
              App Store & Google Play
            </span>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {item.title.replace(' - App Mobile', '')}
          </h3>
          <p className="text-white/60 text-sm mb-4">
            {item.description}
          </p>

          {/* Technologies */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {item.technologies.map((tech, i) => (
              <span 
                key={i}
                className="text-xs text-[#8B5A8B] bg-[#8B5A8B]/10 px-3 py-1 rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* App Store Link */}
          {item.link ? (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#8B5A8B] hover:bg-[#A16DA1] text-white px-6 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(139,90,139,0.4)]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Télécharger
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/60 px-6 py-3 rounded-full">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              En cours de développement
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// MacBook Mockup Component for Web Projects
const MacBookMockup = ({ item, index }) => {
  return (
    <motion.div
      variants={itemVariants}
      className="group"
      data-testid={`portfolio-web-${item.id}`}
    >
      <div className="glass-card rounded-3xl p-6 flex flex-col items-center">
        {/* MacBook Frame */}
        <div className="relative mb-4 w-full">
          {/* Screen bezel */}
          <div className="relative bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-t-xl p-[6px] mx-auto max-w-[340px]">
            {/* Camera */}
            <div className="absolute top-[3px] left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] rounded-full">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-[#1a3a1a] rounded-full" />
            </div>
            
            {/* Screen */}
            <div className="relative bg-black rounded-lg overflow-hidden aspect-[16/10]">
              <img 
                src={item.image_url} 
                alt={item.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Link overlay */}
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#8B5A8B]/30"
                >
                  <span className="bg-[#8B5A8B] text-white px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium shadow-lg">
                    Voir le site
                    <ExternalLink size={14} />
                  </span>
                </a>
              )}
            </div>
          </div>
          
          {/* MacBook body/keyboard */}
          <div className="relative mx-auto max-w-[360px]">
            {/* Hinge */}
            <div className="h-[6px] bg-gradient-to-b from-[#2a2a2a] to-[#3a3a3a] rounded-b-sm mx-8" />
            {/* Body */}
            <div className="h-[10px] bg-gradient-to-b from-[#c0c0c0] to-[#a0a0a0] rounded-b-xl mx-2 shadow-md">
              {/* Trackpad notch */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-[4px] bg-[#888] rounded-t-sm" />
            </div>
          </div>
          
          {/* Glow effect */}
          <div className="absolute inset-0 bg-[#8B5A8B]/10 blur-3xl rounded-full -z-10 group-hover:bg-[#8B5A8B]/20 transition-colors duration-500" />
        </div>

        {/* Project info */}
        <div className="text-center w-full px-2">
          {/* Category badge */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <Monitor className="text-[#8B5A8B]" size={16} />
            <span className="text-xs text-[#8B5A8B] font-medium uppercase tracking-wider">
              {categories.find(c => c.id === item.category)?.label || item.category}
            </span>
          </div>
          
          <h3 className="text-lg font-semibold text-white mb-1">
            {item.title}
          </h3>
          <p className="text-white/60 text-xs mb-3 line-clamp-2">
            {item.description}
          </p>

          {/* Technologies */}
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            {item.technologies.slice(0, 3).map((tech, i) => (
              <span 
                key={i}
                className="text-xs text-[#8B5A8B] bg-[#8B5A8B]/10 px-2 py-0.5 rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Link button */}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#8B5A8B] hover:text-[#A16DA1] text-sm font-medium transition-colors"
            >
              Visiter le site
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/portfolio`);
        setPortfolio(response.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const filteredPortfolio = activeFilter === 'all'
    ? portfolio
    : portfolio.filter(item => item.category === activeFilter);

  return (
    <div className="min-h-screen pt-24" data-testid="portfolio-page">
      <SEO 
        title="Portfolio - Nos realisations"
        description="WEBISULA vous presente les dernieres realisations de sites internet vitrine et e-commerce concus pour les artisans, commercants, TPE et PME de Corse."
        path="/portfolio"
      />
      {/* Hero */}
      <section className="pt-8 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label mb-4"
          >
            Portfolio
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-6"
            data-testid="portfolio-title"
          >
            Nos <span className="text-gradient">réalisations</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/60 max-w-2xl mx-auto"
          >
            Découvrez quelques-uns de nos projets les plus récents réalisés pour nos clients.
          </motion.p>
        </div>
      </section>

      {/* Filter */}
      <section className="px-6 lg:px-12 mb-12">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-3 justify-center"
            data-testid="portfolio-filters"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === cat.id
                    ? 'bg-[#8B5A8B] text-white shadow-[0_0_20px_rgba(139,90,139,0.4)]'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-[#8B5A8B] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPortfolio.length === 0 ? (
            <div className="text-center py-20">
              <Filter className="text-white/20 mx-auto mb-4" size={48} />
              <p className="text-white/40">Projet en cours de fabrication, à retrouver très prochainement ici</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredPortfolio.map((item, index) => (
                <MacBookMockup key={item.id} item={item} index={index} />
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
